import { useEffect, useRef } from 'preact/hooks';
import { syncCartByCartId } from '@/api';
import { Service } from '@/base-api/types';
import { OfferModalInner } from '@/components/offer-modal-inner';
import { useDialog } from '@/contexts/dialog-context';
import { useServiceStore } from '@/contexts/service-context';
import { shouldRunSyncCart } from '@/helpers/cart';
import { log } from '@/utils';

export const useAddToCartEvent = () => {
  const {
    variantId,
    services,
    activeOfferingByServiceId,
    hasActiveOfferings,
    hasServicesAvailable,
    serviceContentById,
  } = useServiceStore();
  const { setContent, setOpen, setOnCloseCallback } = useDialog();

  const onItemAddToCart = async (event: Event) => {
    const detail = (event as CustomEvent<{ cart_id: string }>).detail;
    const cartId = detail.cart_id;

    if (!cartId || !hasServicesAvailable) return;

    if (hasActiveOfferings) {
      const offeringIds = Object.values(activeOfferingByServiceId).filter(
        (id): id is number => id != null,
      );

      await addServiceOfferingsToCart({ variantId, cartId, offeringIds });

      window.location.href = '/cart.php';

      return;
    }

    if (services && services.length) {
      const service = getServiceBySortOrder(services);

      if (!service || !variantId) return;

      setOnCloseCallback(() => {
        window.location.href = '/cart.php';
      });

      setContent(
        <OfferModalInner
          content={serviceContentById[service.id].content.offer_modal}
          service={service}
          cartId={cartId}
          variantId={variantId}
        />,
      );
      setOpen(true);
    }

    if (await shouldRunSyncCart()) {
      log.info('Syncing cart');
      await syncCartByCartId(cartId);
    }
  };

  const callbackRef = useRef(onItemAddToCart);
  callbackRef.current = onItemAddToCart;

  useEffect(() => {
    const handler = (data: Event) => callbackRef.current(data);

    window.itsServices.events.on('cart-item-add', handler);

    return () => {
      window.itsServices.events.off('cart-item-add', handler);
    };
  }, []);
};

async function addServiceOfferingsToCart({
  variantId,
  cartId,
  offeringIds,
}: {
  variantId: number | null;
  cartId: string;
  offeringIds: number[];
}) {
  if (variantId == null) return;

  const payload = {
    cart_id: cartId,
    variant_id: variantId,
    'offering_ids[]': offeringIds,
  };

  await window.itsServices.api.addServiceOfferingsToCart(payload);
}

export function getServiceBySortOrder(loadedServices: Service[]) {
  if (!loadedServices) {
    return null;
  }

  const lowestSortOrderService = loadedServices.reduce(
    (minService, currentService) =>
      currentService.sort_order < minService.sort_order
        ? currentService
        : minService,
  );

  return lowestSortOrderService;
}
